import { AdvancedFilterData } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import { ArrayStringFilterOperator } from "@redotech/redo-views-common/advanced-filters/array-string-filter";
import { AdvancedFilterType } from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import { SortDirection, TableSort } from "@redotech/redo-views-common/table";
import {
  DEFAULT_PAGE_SIZE,
  viewTypes,
} from "@redotech/redo-views-common/views";

export enum ShipmentsSystemView {
  ALL = "all",
  PENDING = "pending",
  IN_TRANSIT = "in-transit",
  PARTIALLY_RECEIVED = "partially-received",
  RECEIVED = "received",
}

export const InboundShipmentStatus = {
  PENDING: "PENDING",
  IN_TRANSIT: "IN_TRANSIT",
  PARTIALLY_RECEIVED: "PARTIALLY_RECEIVED",
  RECEIVED: "RECEIVED",
} as const;
export type InboundShipmentStatus =
  (typeof InboundShipmentStatus)[keyof typeof InboundShipmentStatus];

export const shipmentSortDefault: TableSort = {
  direction: SortDirection.DESC,
  key: "createdAt",
};

const getStatusFilter = (status: InboundShipmentStatus) =>
  ({
    name: "status",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [status],
    operator: ArrayStringFilterOperator.ANY_OF,
  }) as AdvancedFilterData;

const defaultColumns = [
  { key: "shipmentId", width: 150, hidden: false },
  { key: "poNumber", width: 150, hidden: false },
  { key: "status", width: 140, hidden: false },
  { key: "skuCount", width: 80, hidden: false },
  { key: "itemCount", width: 80, hidden: false },
  { key: "receivedCount", width: 100, hidden: false },
  { key: "estimatedArrivalDate", width: 120, hidden: false },
  { key: "actualArrivalDate", width: 120, hidden: false },
  { key: "totalCost", width: 120, hidden: false },
  { key: "location", width: 160, hidden: false },
  { key: "supplier", width: 160, hidden: false },
];

export const StaticSystemViews: Record<
  ShipmentsSystemView,
  viewTypes["shipments"]
> = {
  [ShipmentsSystemView.ALL]: {
    table: "shipments",
    name: ShipmentsSystemView.ALL,
    filters: [],
    columns: defaultColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: shipmentSortDefault,
    position: 0,
    isNotEditable: true,
  },
  [ShipmentsSystemView.PENDING]: {
    table: "shipments",
    name: ShipmentsSystemView.PENDING,
    filters: [getStatusFilter(InboundShipmentStatus.PENDING)],
    columns: defaultColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: shipmentSortDefault,
    position: 0,
  },
  [ShipmentsSystemView.IN_TRANSIT]: {
    table: "shipments",
    name: ShipmentsSystemView.IN_TRANSIT,
    filters: [getStatusFilter(InboundShipmentStatus.IN_TRANSIT)],
    columns: defaultColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: shipmentSortDefault,
    position: 0,
  },
  [ShipmentsSystemView.PARTIALLY_RECEIVED]: {
    table: "shipments",
    name: ShipmentsSystemView.PARTIALLY_RECEIVED,
    filters: [getStatusFilter(InboundShipmentStatus.PARTIALLY_RECEIVED)],
    columns: defaultColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: shipmentSortDefault,
    position: 0,
  },
  [ShipmentsSystemView.RECEIVED]: {
    table: "shipments",
    name: ShipmentsSystemView.RECEIVED,
    filters: [getStatusFilter(InboundShipmentStatus.RECEIVED)],
    columns: defaultColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: shipmentSortDefault,
    position: 0,
  },
};

export function friendlyShipmentViewName(viewName: string): string {
  switch (viewName) {
    case ShipmentsSystemView.ALL:
      return "All";
    case ShipmentsSystemView.PENDING:
      return "Pending";
    case ShipmentsSystemView.IN_TRANSIT:
      return "In Transit";
    case ShipmentsSystemView.PARTIALLY_RECEIVED:
      return "Partially Received";
    case ShipmentsSystemView.RECEIVED:
      return "Received";
    default:
      return viewName;
  }
}
